import {
  Component,
  inject,
  OnInit,
  ChangeDetectorRef,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import {
  ApiRespons,
  ManageOrganizationDrawerParams,
  Title,
  TitleModel,
} from '../../manage-organization.interface';
import { OrgManagementMode } from '../../manage-organization.enums';
import { DrawerService } from '../../../../core/services/drawer.service';
import { ManageOrganizationService } from '../../manage-organization.service';
import { finalize, Observable, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { CommonService } from '../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../core/models/error.models';
import { AuditContextService } from '../../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../audit-logs/audit-logs.interface';

@Component({
  selector: 'app-titles',
  standalone: true,
  imports: [
    NzTableModule,
    SvgIconComponent,
    NzModalModule,
    NzButtonModule,
    CommonModule,
    FormsModule,
    ConfirmDialogComponent,
    FormsModule,
    NzModalModule,
    NzInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './titles.component.html',
})
export class TitlesComponent implements OnInit, OnDestroy {
  listOfColumn = [
    {
      title: 'Title',
      key: 'title',
      compare: (a: Title, b: Title) => a.title.localeCompare(b.title),
    },
    {
      title: '',
      compare: null,
    },
  ];
  isDeleting = false;

  constructor(
    private readonly drawerService: DrawerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly ngZone: NgZone,
    private readonly commonService: CommonService,
    private readonly auditContextService: AuditContextService
  ) {
    this.setupDrawerParams();
  }

  titleForm!: FormGroup;
  editingIndex = -1;
  loading = false;
  errorMessage = '';
  isVisible = false;
  deleteId = 0;
  isUpdating: Record<string, boolean> = {};
  readonly titleMode = OrgManagementMode;
  private readonly destroy$ = new Subject<void>();
  private readonly titleService = inject(ManageOrganizationService);
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
    organizationName: '',
  };
  sortOrder: number | undefined = undefined;
  sortField = '';
  originalData: Title = {
    id: 0,
    title: '',
    organizationId: 0,
  };
  titles$ = this.titleService.titles$;
  totalRow = 0;

  ngOnInit(): void {
    this.titleForm = this.fb.group({
      titles: this.fb.array([]),
    });
    this.titles$.subscribe((data: ApiRespons<Title> | null) => {
      if (data && data?.result.length > 0) {
        this.totalRow = data.result.length;
        this.createFormArray(data?.result);
      }
    });
  }

  get isEditMode(): boolean {
    return this.orgParams.mode === this.titleMode.EDIT;
  }
  get isViewMode(): boolean {
    return this.orgParams.mode === OrgManagementMode.VIEW;
  }
  get titles(): FormArray {
    return this.titleForm.get('titles') as FormArray;
  }

  createFormArray(titles: Title[]): void {
    const formGroups = titles.map(t =>
      this.fb.group({
        id: [t.id],
        title: [
          { value: t.title, disabled: this.isViewMode },
          [Validators.required, Validators.maxLength(100)],
        ],
        organizationId: [t.organizationId],
      })
    );
    this.titles.clear();
    formGroups.forEach(group => this.titles.push(group));
  }

  addNewRow(): void {
    const titles = this.titleForm.get('titles') as FormArray;
    const newRow = this.fb.group({
      id: [null],
      title: [
        { value: '', disabled: this.isViewMode },
        [Validators.required, Validators.maxLength(100)],
      ],
      organizationId: [this.orgParams.organizationId],
    });

    titles.insert(0, newRow);
  }

  removeRow(index: number): void {
    const titles = this.titleForm.get('titles') as FormArray;
    const titleFormGroup = this.titles.at(index);
    titleFormGroup.get('title')?.disable();
    titles.removeAt(index);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { sort } = params;
    const currentSort = sort.find(item => item.value !== null);
    if (currentSort) {
      this.sortField = currentSort.key;
      this.sortOrder = currentSort.value === 'ascend' ? 1 : 0;
    } else {
      this.sortField = '';
      this.sortOrder = undefined;
    }

    this.loadData();
  }

  loadData(): void {
    this.loading = true;

    this.titleService
      .getTitles({
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        organizationId: this.orgParams.organizationId,
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          this.cdr.detectChanges();
          if (response?.result) {
            this.totalRow = response.result.length;
            this.createFormArray(response.result);
            this.orgParams.mode = OrgManagementMode.VIEW;
          }

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (error: Error) => {
          this.errorMessage = error.message;
          setTimeout(() => (this.errorMessage = ''), 3000);
        },
      });
  }

  disableControl(index: number) {
    return this.editingIndex !== index;
  }

  addTitle() {
    if (this.orgParams.mode !== this.titleMode.CREATE) {
      this.addNewRow();
    }
    this.editingIndex = 0;
    this.orgParams.mode = this.titleMode.CREATE;
    this.isUpdating[0] = true;
    const titleFormGroup = this.titles.at(0);
    titleFormGroup.get('title')?.enable();
    this.toggleDisable();
  }

  SubmitTitle(index: number) {
    const titles = this.titleForm.get('titles') as FormArray;
    const row = titles.at(index); // Get the FormGroup at the given index
    const data = row.value;
    const payload: TitleModel = {
      title: data.title,
      organizationId: this.orgParams.organizationId,
    };
    const _modifiedFields = this.buildTitleModifiedFields(payload);
    _modifiedFields.push({
      field: 'organizationId',
      oldValue: this.orgParams.organizationName ?? '',
      newValue: this.orgParams.organizationName ?? '',
      fieldLabel: 'Organization',
      isSensitive: false,
    });
    const apiPayload: TitleModel = {
      ...payload,
      _modifiedFields,
    };
    this.loading = true;
    const operation$: Observable<ApiRespons<Title | TitleModel>> = this
      .isEditMode
      ? this.titleService.updateTitle(data.id, apiPayload)
      : this.titleService.createTitle(apiPayload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          if (this.isEditMode) this.isUpdating[index] = false;
        })
      )
      .subscribe({
        next: () => {
          this.cdr.detectChanges();
          this.commonService.notify(
            'success',
            `Title ${this.isEditMode ? 'updated' : 'created'} successfully`
          );
          this.loadData();
          this.isUpdating[index] = false;
          this.orgParams.mode = this.titleMode.VIEW;
          this.editingIndex = -1;
          this.cdr.detectChanges();
        },
        error: (error: ErrorResponse) => {
          this.loading = false;
          this.handleError(error);
        },
      });
  }

  private buildTitleModifiedFields(payload: TitleModel): AuditFieldChange[] {
    const { original, updated } = this.getTitleAuditContext(payload);
    const enrichedOriginal = this.getEnrichedTitleAuditPayload(original);
    const enrichedUpdated = this.getEnrichedTitleAuditPayload(updated);

    return this.auditContextService.buildFieldChanges(
      enrichedOriginal,
      enrichedUpdated,
      {
        sensitiveFields: [],
        labelMap: this.getTitleAuditLabelMap(),
        excludeFields: ['id', 'organizationId'],
      }
    );
  }

  private getTitleAuditContext(payload: TitleModel): {
    original: Partial<TitleModel>;
    updated: Partial<TitleModel>;
  } {
    if (!this.isEditMode) {
      return {
        original: {},
        updated: payload,
      };
    }

    return {
      original: {
        title: this.originalData.title,
        organizationId: this.originalData.organizationId,
      },
      updated: payload,
    };
  }

  private getEnrichedTitleAuditPayload(
    payload: Partial<TitleModel>
  ): Record<string, unknown> {
    return {
      ...payload,
      organizationId: this.getOrganizationAuditValue(payload.organizationId),
    };
  }

  private getTitleAuditLabelMap(): Record<string, string> {
    return {
      title: 'Title',
      organizationId: 'Organization',
    };
  }

  private getOrganizationAuditValue(
    organizationId: number | undefined
  ): string {
    if (!organizationId) {
      return '';
    }

    return this.orgParams.organizationName || organizationId.toString();
  }

  showDeleteModal(id: number): void {
    this.isVisible = true;
    this.deleteId = id;
  }
  handleOkMiddle(): void {
    this.deleteOrganization(this.deleteId);
  }
  handleCancelMiddle(): void {
    this.isVisible = false;
    this.deleteId = 0;
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  editTitle(index: number): void {
    const titleFirstFormGroup = this.titles.at(0);
    if (titleFirstFormGroup.value.id === null) {
      this.removeRow(0);
      index = index - 1;
    }
    this.orgParams.mode = OrgManagementMode.EDIT;
    this.editingIndex = index;
    const titleFormGroup = this.titles.at(index);
    titleFormGroup.get('title')?.enable();
    // Get the value of the entire form group (returns an object of all controls)
    const titleValue = titleFormGroup.value;
    this.originalData = titleValue;
    this.isUpdating[index] = true;
    this.toggleDisable();
  }

  toggleDisable() {
    const titlesArray = this.titleForm.get('titles') as FormArray;

    titlesArray.controls.forEach((control, index) => {
      if (this.editingIndex === index) {
        control.get('title')?.enable();
      } else {
        control.get('title')?.disable();
      }
    });
  }

  onCancel(index: number) {
    if (this.orgParams.mode === OrgManagementMode.CREATE) {
      this.removeRow(index);
    }
    this.isUpdating[index] = false;
    if (this.originalData && this.orgParams.mode !== OrgManagementMode.CREATE) {
      this.titles.at(index).patchValue(this.originalData);
    }
    this.editingIndex = -1;
    this.toggleDisable();
    this.orgParams.mode = OrgManagementMode.VIEW;
  }
  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
    this.cdr.detectChanges();
    this.loading = false;
  }

  deleteOrganization(id: number): void {
    const titles = this.titleForm.get('titles') as FormArray;
    const row = titles.at(id); // Get the FormGroup at the given index
    const data = row.value;
    if (data.id) {
      this.isDeleting = true;
      this.titleService
        .deleteTitle(data.id)
        .pipe(
          finalize(() => (this.isDeleting = false)),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => {
            this.loadData();
            this.cdr.detectChanges();
            this.commonService.notify('success', 'Title deleted successfully');
            this.isVisible = false;
            this.isDeleting = false;
            this.orgParams.mode = OrgManagementMode.VIEW;
          },
          error: error => {
            this.isVisible = false;
            this.isDeleting = false;
            this.cdr.detectChanges();
            this.commonService.notify('error', error.message);
          },
        });
    } else {
      this.isDeleting = false;
      this.isVisible = false;
      this.orgParams.mode = this.titleMode.VIEW;
      this.removeRow(this.deleteId);
      this.editingIndex = -1;
    }
    this.deleteId = 0;
    this.toggleDisable();
  }

  onEnterKey(index: number, event: Event): void {
    // Prevent the default Enter key behavior
    event.preventDefault();

    // Optionally, you can trigger save logic for the current row
    if (!this.isViewMode && this.titles.controls[index].valid) {
      this.SubmitTitle(index);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
